import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAllFx } from "../../hooks/fx";
import { storePriceGbp, wfPayout } from "../../helpers/wfPayout";
import { STATE_TONE, checkedMarketplaces, checkedTooFew, marginCheck, ownChannelPrice } from "./priceCheck";
import { chipLabel, editPriceUrl, listingCurrency } from "./priceCheckCopy";
import useAccountChecks from "./useAccountChecks";
import usePriceCheckContext from "./usePriceCheckContext";
import PriceCheckPanel from "./PriceCheckPanel";
import { PRICE_REVIEW_CHANGED } from "../../rc/PriceReviewBanner";
import "./price-check.scss";

const CHIP_STATES = ["above", "below", "in_range", "reviewed"];
// …plus "checked, too few to compare" (grey) — a dated check with 0 or 1 listings.
const showChip = (c) => CHIP_STATES.includes(c.state) || checkedTooFew(c);

const pct = (v) => `${Math.round(v * 100)}%`;
// "Patek Philippe" → "Patek", "Audemars Piguet" → "AP" — the pill stays one line.
const shortBrand = (name) => {
  if (!name) return "Brand";
  if (/^audemars/i.test(name)) return "AP";
  return String(name).split(/\s+/)[0];
};

/**
 * Price check panel at the top of a listing card's photo, in price-review mode:
 * one state-coloured row per marketplace with a verdict — CH24 UK, each other
 * Chrono24 account the listing is switched on for, eBay, WatchFinder — and a
 * red pill when the CH24 UK price, read as an order margin over the 3M trade
 * median, is under the brand's PriceIQ order margin. A row click opens the
 * Price check popup on that tab. Keeps its own copy of the listing so a price
 * set or a review from the popup re-colours it without a reload.
 */
export default function PriceCheckChips({ listing: initial, onListingChange }) {
  const [listing, setListing] = useState(initial);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => setListing(initial), [initial]);

  const { checks: accountChecks } = useAccountChecks(listing, { enabled: !!listing?.id });
  const context = usePriceCheckContext(listing, { enabled: !!listing?.id });

  const chips = useMemo(() => {
    const base = checkedMarketplaces(listing)
      .filter(showChip)
      .map((c) => ({ key: c.marketplace, check: c, currency: listingCurrency(listing) }));
    const accounts = accountChecks
      .filter((a) => a.isListed && showChip(a.check))
      .map((a) => ({ key: a.key, check: a.check, currency: a.check.currency }));
    // Account rows sit right after the CH24 UK row.
    const ukAt = base.findIndex((c) => c.key === "chrono");
    return ukAt === -1 ? [...accounts, ...base] : [...base.slice(0, ukAt + 1), ...accounts, ...base.slice(ukAt + 1)];
  }, [listing, accountChecks]);

  const margin = useMemo(() => marginCheck(ownChannelPrice(listing, "chrono"), context), [listing, context]);
  const lowMargin = margin?.low ? margin : null;

  // WatchFinder payout below the store price — the red "WF Payout" in the form.
  // Prices on the listing are minor units; the payout rule works in GBP.
  const history = useHistory();
  const { rates } = useAllFx(listing?.is_wf ? "GBP" : null);
  const wfLow = useMemo(() => {
    const on = listing?.is_wf === true || listing?.is_wf === 1 || listing?.is_wf === "1";
    if (!on) return null;
    const store = storePriceGbp((Number(listing?.price) || 0) / 100, listing?.currency || "GBP", rates);
    const payout = wfPayout((Number(listing?.wf_price) || 0) / 100);
    return store !== null && payout !== null && payout > 0 && payout < store ? { store, payout, gap: store - payout } : null;
  }, [listing, rates]);

  if (!listing || (chips.length === 0 && !lowMargin && !wfLow)) return null;

  const onChange = (updated) => {
    setListing(updated);
    onListingChange?.(updated);
    window.dispatchEvent(new CustomEvent(PRICE_REVIEW_CHANGED));
  };

  return (
    // Clicks inside the popup bubble through React's portal to this wrapper —
    // stop them here so they never reach the card (which opens the listing).
    <div className="pc-cardpanel" aria-label="Price check" onClick={(e) => e.stopPropagation()}>
      {chips.map(({ key, check, currency }) => (
        <button
          key={key}
          type="button"
          className={`pc-chip pc-tone--${STATE_TONE[check.state]}`}
          title={`${check.label} price check — open details`}
          onClick={() => setOpenKey(key)}
        >
          <span className="pc-chip__dot" aria-hidden="true" />
          <span className="pc-chip__text">{chipLabel(check, currency)}</span>
        </button>
      ))}
      {lowMargin ? (
        <button
          type="button"
          className="pc-chip pc-tone--danger pc-chip--margin"
          title={`CH24 price vs ${lowMargin.window} trade median is a ${pct(lowMargin.margin)} order margin — under ${lowMargin.brand}'s ${pct(lowMargin.brandMargin)} PriceIQ order margin`}
          onClick={() => setOpenKey("chrono")}
        >
          <span className="pc-chip__dot" aria-hidden="true" />
          <span className="pc-chip__text">{`Margin ${pct(lowMargin.margin)} · ${shortBrand(lowMargin.brand)} ${pct(lowMargin.brandMargin)}`}</span>
        </button>
      ) : null}
      {wfLow ? (
        <button
          type="button"
          className="pc-chip pc-tone--danger pc-chip--wf-payout"
          title={`WatchFinder pays out £${wfLow.payout.toLocaleString("en-GB")} — £${wfLow.gap.toLocaleString("en-GB")} below the store price £${wfLow.store.toLocaleString("en-GB")}. Open the WatchFinder tab.`}
          onClick={() => history.push(editPriceUrl(listing.id, "wf"))}
        >
          <span className="pc-chip__dot" aria-hidden="true" />
          <span className="pc-chip__text">{`WF payout £${wfLow.gap.toLocaleString("en-GB")} below store`}</span>
        </button>
      ) : null}
      <PriceCheckPanel
        open={!!openKey}
        marketplace={openKey || "chrono"}
        listing={listing}
        onClose={() => setOpenKey(null)}
        onListingChange={onChange}
      />
    </div>
  );
}
